import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AccordionData } from '@contracts';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import Colors from '../Colors';
import Text from '../Text';
import Trans from '../Trans';
import useDesktop from '../useDesktop';
import AccordionItemExpandButton from './AccordionItemExpandButton';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface AccordionItemProps {
  itemData: AccordionData;
  isSelected: boolean;
  setSelectedIndex: (index: number) => void;
  index?: number;
  isFirst?: boolean;
  isLast?: boolean;
  dividerColor?: string;
  titleUppercase?: boolean;
  dividerIsSolid?: boolean;
  iconsSize?: string;
  iconsColor?: string;
  itemPadding?: string;
  contentPadding?: string;
  itemPaddingMobile?: string;
  contentPaddingMobile?: string;
  textColor?: string;
  areaIndexKey?: string;
  topDivider?: boolean;
  mobileTopDivider?: boolean;
  bottomDivider?: boolean;
  mobileBottomDivider?: boolean;
  isUsingArrows?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  isSelected,
  itemData,
  index,
  isFirst,
  isLast,
  dividerColor,
  dividerIsSolid,
  textColor,
  setSelectedIndex,
  titleUppercase,
  topDivider,
  mobileTopDivider,
  bottomDivider,
  mobileBottomDivider,
  iconsSize = '16px',
  iconsColor = Colors.gold,
  itemPadding = '16px 0',
  contentPadding = '0',
  itemPaddingMobile = '16px 0',
  contentPaddingMobile = '0',
  areaIndexKey = 'faq',
  isUsingArrows = false
}) => {
  const ariaIndex = `${areaIndexKey}${index}_desc`;
  const [innerHeight, setInnerHeight] = useState(0);
  const contentToMeasure = useRef<HTMLDivElement>(null);
  const isMobile = !useDesktop(768);
  const hasTopDivider = !isFirst || (isMobile && mobileTopDivider) || (!isMobile && topDivider);
  const hasBottomDivider = isLast && ((isMobile && mobileBottomDivider) || (!isMobile && bottomDivider));

  useEffect(() => {
    if (contentToMeasure.current) {
      setInnerHeight(contentToMeasure.current.clientHeight + 3);
    }
  }, [itemData.prismicContent, itemData.prismicHtmlContent, itemData.contentText]);

  const questionHtml = useReplacedInnerHtml(itemData.title);

  return (
    <Container>
      <DT onClick={() => setSelectedIndex(index ?? 0)} $textColor={textColor} $titleUppercase={titleUppercase} $isSelected={isSelected}>
        {hasTopDivider ? <Line $dividerColor={dividerColor} $dividerIsSolid={dividerIsSolid} /> : null}
        <Button $itemPadding={itemPadding} $itemPaddingMobile={itemPaddingMobile} aria-expanded={isSelected} aria-controls={ariaIndex}>
          <p dangerouslySetInnerHTML={{ __html: questionHtml }} aria-label={`toggle ${areaIndexKey} answer`}></p>
          <AccordionItemExpandButton isUsingArrows={isUsingArrows} isSelected={isSelected} iconsColor={iconsColor} iconsSize={iconsSize} />
        </Button>
      </DT>
      <DD
        $isSelected={isSelected}
        $ddHeight={innerHeight}
        $textColor={textColor}
        $contentPadding={contentPadding}
        $contentPaddingMobile={contentPaddingMobile}
      >
        <ExpandableContentWrap ref={contentToMeasure} id={ariaIndex}>
          {itemData.contentText ? (
            <Typography $variant={TextVariant.BodyRegularMD}>
              <Trans transKey={itemData.contentText} />
            </Typography>
          ) : (
            <Text
              text={!itemData.prismicHtmlContent && typeof itemData.prismicContent === 'string' ? itemData.prismicContent : undefined}
              richText={itemData.prismicHtmlContent}
              linkTargetBlank
              fontSize="16px"
              fontSizeMobile="14px"
            />
          )}
          {itemData.children}
        </ExpandableContentWrap>
      </DD>
      {hasBottomDivider ? <Line $dividerColor={dividerColor} $dividerIsSolid={dividerIsSolid} /> : null}
    </Container>
  );
};

export default AccordionItem;

const Container = styled.div``;

const DT = styled.dt<{ $textColor?: string; $titleUppercase?: boolean; $isSelected?: boolean }>`
  p {
    text-align: left;
    color: ${(props) => props.$textColor || Colors.navyBlue};
    font-family: 'Domaine';
    font-size: 16px;
    font-style: normal;
    font-weight: ${(props) => (props.$titleUppercase || props.$isSelected ? '600' : '400')};
    line-height: 120%; /* 19.2px */
    letter-spacing: -0.16px;

    ${(props) => (props.$titleUppercase ? TEXT_STYLES[TextVariant.UpperText4] : '')};

    @media (max-width: 768px) {
      ${(props) => (props.$titleUppercase ? TEXT_STYLES[TextVariant.UpperText5] : TEXT_STYLES[TextVariant.BodyRegularLG])};
      font-weight: ${(props) => (props.$titleUppercase || props.$isSelected ? '600' : '400')};
    }
  }
`;

const Button = styled.button<{ $itemPadding: string; $itemPaddingMobile?: string }>`
  border: none;
  padding: ${(props) => props.$itemPadding};
  background: none;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    padding-right: 3px;
  }

  &:hover {
    cursor: pointer;
  }

  @media only screen and (min-width: 769px) {
    &:focus {
      outline: 0;
    }
  }

  @media (max-width: 768px) {
    padding: ${(props) => props.$itemPaddingMobile};
  }
`;

const DD = styled.dd<{ $textColor?: string; $isSelected?: boolean; $contentPadding?: string; $ddHeight?: number; $contentPaddingMobile?: string }>`
  margin: ${(props) => (props.$isSelected ? '0 10px 16px 0' : '0 10px 0 0')};
  overflow: hidden;
  transition: height 0.2s ease-in-out, margin 0.2s ease-in-out;
  height: ${(props) => (props.$isSelected ? `${props.$ddHeight}px` : 0)};
  padding: ${(props) => props.$contentPadding};

  p {
    color: ${(props) => props.$textColor || Colors.navyBlue};
  }

  @media (max-width: 768px) {
    padding: ${(props) => props.$contentPaddingMobile};
  }
`;

const Line = styled.hr<{ $dividerIsSolid?: boolean; $dividerColor?: string }>`
  width: 100%;
  border: none;
  border-top: ${(props) => (props.$dividerIsSolid ? '1px solid ' : '1px dashed ')}
    ${(props) => (props.$dividerColor ? props.$dividerColor : '#003a1b')};
  height: 1px;
  margin: 0;
`;

const ExpandableContentWrap = styled.div`
  p {
    margin-bottom: 10px;
    white-space: pre-line;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${Colors.lilac};
    font-size: inherit !important;
  }
`;
